import {
  CompanyCreditProfile,
  ISO,
  PRODUCT_TYPE,
  Side,
  TECHNOLOGY_TYPE,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import { BidDTO } from './bidApi';
import { OfferDTO } from './offerApi';
import { ApiTags, CommonTagIds, MarketDepthTagIds } from './tags';
import { MatchingCriteriaFlags } from './types';

export type MarketDepthDto = {
  sideId: string;
  sideName: string;
  uniqueId: number;
  statusId: string;
  statusName: string;
  price: number;
  isoId: string;
  isoName: string;
  settlementPoint: string;
  startDate: string;
  targetCod: string;
  tenorMonths: number;
  technologyTypeId: string;
  technologyTypeName: string;
  productTypeId: string;
  productTypeName: string;
  contractTypeId: string;
  contractTypeName: string;
  recProgramId: string;
  recProgramName: string;
  creditProfileId: string;
  creditProfileName: string;
  creditDescription: string;
  companyId: string;
  volumeMwh?: number;
  buyerPostCodCollateral?: number;
  expectedSettlementPrice?: number;
  p50ForecastVolume?: number;
  impliedCarbonPrice?: number;
  offeredCapacityMw?: number;
  sellerPostCodCollateral?: number;
  projectId?: string;
  projectName?: string;
  cod?: string;
  matching247?: boolean;
  bessOption?: boolean;
  capacityRights?: boolean;
  escalator?: boolean;
  nodalSettlement?: boolean;
  priceCap?: boolean;
  priceCollar?: boolean;
  priceFloor?: boolean;
  realTimeDataAccess?: boolean;
  ssa?: boolean;
  taxCreditTransferability?: boolean;
  upfrontPayment?: boolean;
  upsideRevenueShare?: boolean;
} & MatchingCriteriaFlags;

export type GetMarketDepthParams = {
  sideView: Side;
  companyId: string;
  isos?: ISO[];
  technologies?: TECHNOLOGY_TYPE[];
  productTypes?: PRODUCT_TYPE[];
  contractTypes?: string[];
  applicableRecPrograms?: string[];
  hubZoneMatrixIds?: number[];
  creditProfiles?: CompanyCreditProfile[];
  fixedPriceMin?: number;
  fixedPriceMax?: number;
  tenorStartDateMin?: string;
  tenorStartDateMax?: string;
};

const BASE_URL = '/api/v1/market-depth';

export const marketDepthApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getMarketDepth: build.query<MarketDepthDto[], GetMarketDepthParams>({
      query: ({
        sideView,
        companyId,
        isos,
        technologies,
        productTypes,
        contractTypes,
        applicableRecPrograms,
        hubZoneMatrixIds,
        creditProfiles,
        fixedPriceMin,
        fixedPriceMax,
        tenorStartDateMin,
        tenorStartDateMax,
      }) => {
        const searchParams = new URLSearchParams();

        if (isos) {
          isos.forEach(iso => searchParams.append('isos', iso));
        }
        if (technologies) {
          technologies.forEach(tech =>
            searchParams.append('technologies', tech)
          );
        }
        if (productTypes) {
          productTypes.forEach(type =>
            searchParams.append('productTypes', type)
          );
        }
        if (contractTypes) {
          contractTypes.forEach(type =>
            searchParams.append('contractTypes', type)
          );
        }
        if (applicableRecPrograms) {
          applicableRecPrograms.forEach(program =>
            searchParams.append('applicableRecPrograms', program)
          );
        }
        if (hubZoneMatrixIds) {
          hubZoneMatrixIds.forEach(id =>
            searchParams.append('hubZoneMatrixIds', id.toString())
          );
        }
        if (creditProfiles) {
          creditProfiles.forEach(profile =>
            searchParams.append('creditProfiles', profile)
          );
        }
        if (fixedPriceMin !== undefined) {
          searchParams.append('fixedPriceMin', fixedPriceMin.toString());
        }
        if (fixedPriceMax !== undefined) {
          searchParams.append('fixedPriceMax', fixedPriceMax.toString());
        }
        if (tenorStartDateMin) {
          searchParams.append('tenorStartDateMin', tenorStartDateMin);
        }
        if (tenorStartDateMax) {
          searchParams.append('tenorStartDateMax', tenorStartDateMax);
        }

        const queryString = searchParams.toString();
        const suffix = queryString ? `?${queryString}` : '';
        const url = `${BASE_URL}${suffix}`;

        return {
          url,
          headers: {
            'X-Side-View': sideView,
            'X-Company-Id': companyId,
          },
        };
      },
      providesTags: (result, error, { companyId, sideView }) => [
        {
          type: ApiTags.MARKET_DEPTH,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
      ],
    }),
    getMatchingBids: build.query<
      BidDTO[],
      { offerId: number; companyId: string }
    >({
      query: ({ offerId, companyId }) => ({
        url: `${BASE_URL}/matching-bids?offerId=${offerId}`,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      providesTags: (result, error, { offerId }) => [
        {
          type: ApiTags.MARKET_DEPTH,
          id: MarketDepthTagIds.MATCHING_BIDS,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: `${MarketDepthTagIds.MATCHING_BIDS}-${offerId}`,
        },
      ],
    }),
    getMatchingOffers: build.query<
      OfferDTO[],
      { bidId: number; companyId: string }
    >({
      query: ({ bidId, companyId }) => ({
        url: `${BASE_URL}/matching-offers?bidId=${bidId}`,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      providesTags: (result, error, { bidId }) => [
        {
          type: ApiTags.MARKET_DEPTH,
          id: MarketDepthTagIds.MATCHING_OFFERS,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: `${MarketDepthTagIds.MATCHING_OFFERS}-${bidId}`,
        },
      ],
    }),
  }),
});

export const {
  useGetMarketDepthQuery,
  useGetMatchingBidsQuery,
  useGetMatchingOffersQuery,
} = marketDepthApi;
